import {
  WAVE_APP_ID,
  WaveCloudAPI,
  activeTodoMentionQuery,
  listTodoMentionCandidates,
  participantMentionLabel,
  type WaveDocEditor,
  type WaveParticipantRecord,
  type WaveTodoMentionCandidate,
} from "@vida/wave-core";
import { createWaveApiConfig } from "./waveSession";
import { getWaveWhaleAgentId, isWhaleConfigured } from "./waveClientConfig";
import { runWhaleThreadMission, type WhaleThreadMissionResult } from "./waveWhale";

export type WhaleTeamAgent = {
  id: string;
  name: string;
  label: string;
  icon: string;
  iconS3Key?: string | null;
};

let cachedAgents: WhaleTeamAgent[] | null = null;
let cacheAt = 0;
const CACHE_MS = 60_000;

function agentMentionLabel(name: string): string {
  const first = name.trim().split(/\s+/)[0] ?? name.trim();
  return first.replace(/[^\p{L}\p{N}._-]/gu, "") || name.trim().slice(0, 12);
}

export async function fetchWhaleTeamAgents(): Promise<WhaleTeamAgent[]> {
  if (cachedAgents && Date.now() - cacheAt < CACHE_MS) return cachedAgents;
  if (!isWhaleConfigured()) return [];
  try {
    const cfg = createWaveApiConfig();
    const whaleId = getWaveWhaleAgentId().trim();
    const rows = await WaveCloudAPI.listOceanAgents(cfg, 80);
    cachedAgents = rows
      .filter(
        (row) =>
          row.enabled &&
          row.appIds.includes(WAVE_APP_ID) &&
          // Altri orchestratori esclusi; Whale (l'orchestratore di Wave) è menzionabile.
          (row.role !== "ORCHESTRATOR" || row.id === whaleId),
      )
      .sort((a, b) => {
        if (a.id === whaleId) return -1;
        if (b.id === whaleId) return 1;
        return a.name.localeCompare(b.name);
      })
      .map((row) => ({
        id: row.id,
        name: row.name,
        label: agentMentionLabel(row.name),
        icon: row.icon?.trim() || "🤖",
        iconS3Key: row.iconS3Key?.trim() || null,
      }));
    cacheAt = Date.now();
    return cachedAgents;
  } catch {
    return cachedAgents ?? [];
  }
}

export function whaleTeamAgentCandidates(
  agents: WhaleTeamAgent[],
  query = "",
): WaveTodoMentionCandidate[] {
  const q = query.trim().toLowerCase();
  return agents
    .filter((agent) => {
      if (!q) return true;
      const hay = `${agent.label} ${agent.name}`.toLowerCase();
      return hay.includes(q) || agent.label.toLowerCase().startsWith(q);
    })
    .map((agent) => ({
      ref: agent.id,
      label: agent.label,
      glyph: agent.icon,
      kind: "agent" as const,
      iconS3Key: agent.iconS3Key ?? undefined,
    }));
}

export function listChatMentionCandidates(
  participants: WaveParticipantRecord[],
  agents: WhaleTeamAgent[],
  query = "",
): WaveTodoMentionCandidate[] {
  const humans = listTodoMentionCandidates(participants, query).filter(
    (c) => c.kind === "participant",
  );
  const agentRows = whaleTeamAgentCandidates(agents, query);
  // Agenti prima: target principale delle menzioni in chat (e mai sotto il fold).
  return [...agentRows, ...humans];
}

export function parseChatAgentMention(
  body: string,
  agents: WhaleTeamAgent[],
): WhaleTeamAgent | null {
  const match = body.match(/@([\p{L}\p{N}._-]+)/u);
  if (!match) return null;
  const token = match[1].toLowerCase();
  return (
    agents.find(
      (agent) =>
        agent.label.toLowerCase() === token ||
        agent.name.toLowerCase().startsWith(token),
    ) ?? null
  );
}

export { activeTodoMentionQuery, participantMentionLabel };

export async function startWhaleMissionFromChat(input: {
  waveId: string;
  spaceId: string;
  body: string;
  editor: WaveDocEditor;
  launchedBy: string;
  agents?: WhaleTeamAgent[];
}): Promise<WhaleThreadMissionResult | null> {
  const agents = input.agents ?? (await fetchWhaleTeamAgents());
  const mentioned = parseChatAgentMention(input.body, agents);
  if (!mentioned) return null;
  if (!isWhaleConfigured()) return null;

  const goal = input.body.trim();
  // @whale = orchestratore (può delegare). Altri agenti = invocazione diretta:
  // l'agente menzionato è l'esecutore, così icona e nome sono i suoi (non Whale).
  const isWhale = mentioned.id === getWaveWhaleAgentId().trim();
  return runWhaleThreadMission({
    waveId: input.waveId,
    spaceId: input.spaceId,
    goal,
    launchedBy: input.launchedBy,
    editor: input.editor,
    ...(isWhale
      ? {}
      : {
          invokeAgentId: mentioned.id,
          mentionLabel: mentioned.label,
          mentionAgentName: mentioned.name,
        }),
  });
}
